import { useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  describeTranscriptEditError,
  editCaptureTranscript,
} from '../../data/writers/edit-capture-transcript';
import { requestCaptureOrganizationInBackground } from '../../data/organize-client';
import { reportError } from '../../lib/monitoring';
import { formatInstant } from '../../lib/timezone';
import { useSession } from '../auth/session';
import type { CaptureTranscriptVersionRow } from '../../data/database.types';
import { isBlankTranscript } from './capture-status';
import './captures.css';
import { errorCodeOf, isStaleWriteRefusal } from '../../lib/error-classification';

/**
 * CORRECTING A TRANSCRIPT — M6 / S06-03.
 *
 * **Edit → fix the words → Save.** That is the whole feature. Brandon is
 * fixing what speech-to-text misheard, not managing revisions, so there is no
 * confirmation, no reason field, no formatting, no diff and no revert.
 *
 * VIEW-FIRST, exactly as PT §7 requires of the review screen this echoes: the
 * transcript is text until he deliberately taps Edit. The keyboard never opens
 * on its own.
 *
 * WHAT IS TRUE UNDERNEATH, so the tap can stay a tap:
 *
 *   **Nothing is ever overwritten.** A save appends version N+1; version N
 *   stays exactly as it was stored, down to its whitespace. The database
 *   agrees rather than being trusted to: no application role holds UPDATE or
 *   DELETE on `capture_transcript_version` (INV-T05), so "previous versions
 *   are preserved" is a privilege, not a promise this component keeps.
 *
 *   **The version he is correcting is sent with the correction.** If the
 *   transcript moved since this screen loaded, the server refuses rather than
 *   burying an edit he never read — and then we show him what it says now.
 *
 *   **Saving the same words does nothing at all**: no version, no Work Log
 *   event, no "Edited". An edit recorded in the history that did not happen
 *   would be a lie in the one record whose worth is that it holds none.
 *
 * EVERY WORD SHOWN HERE COMES FROM `capture_transcript_version` — the current
 * text and every previous version alike. `work_log_event.detail` quotes the
 * transcript for provenance and is NEVER read as content (D-016); a copy is
 * allowed to fall behind the thing it copied.
 */
export function CaptureTranscriptSection({
  captureId,
  transcript,
  version,
  previousVersions,
  onSaved,
}: {
  captureId: string;
  /** Server truth: the CURRENT (highest) version's body, or null if none. */
  transcript: string | null;
  /** Its version number — the concurrency guard, and what makes "Edited" true. */
  version: number | null;
  /** Everything older, newest first. Read-only, always. */
  previousVersions: readonly CaptureTranscriptVersionRow[];
  /** Reload Capture + History. Awaited, so nothing shows as saved before it is. */
  onSaved: () => Promise<void>;
}) {
  const client = getSupabaseClient();
  const { profile } = useSession();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrevious, setShowPrevious] = useState(false);

  const timeZone = profile?.timezone ?? null;
  const canEdit = transcript !== null && version !== null;
  const edited = version !== null && version > 1;
  const blank = isBlankTranscript(draft);

  const startEditing = (): void => {
    if (transcript === null) return;
    setDraft(transcript);
    setError(null);
    setEditing(true);
  };

  // CANCEL IS A LOCAL EVENT. No request, no version, no Work Log entry —
  // backing out of an edit is not an action the record should remember.
  const cancelEditing = (): void => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const save = async (): Promise<void> => {
    if (busy || !client || version === null || blank) return;

    setBusy(true);
    setError(null);
    try {
      await editCaptureTranscript(client, captureId, draft, version);
      // Truth first, then the screen. The reload is what changes the visible
      // words, so nothing is shown as corrected until the server says it is —
      // and this is also what puts "Transcript edited" at the top of History
      // without reopening the Capture.
      await onSaved();

      // THE CORRECTED WORDS MAY DESCRIBE A DIFFERENT PLACE, so organization is
      // asked to run again for the new version (M7 / S07-02). Fired and
      // forgotten: **the transcript edit has already succeeded**, and a
      // correction to source truth is never undone, delayed or reported as
      // failed because a derived value could not be recomputed.
      //
      // The endpoint decides admission — a genuinely new transcript version is
      // always eligible, however many attempts an older one consumed — so
      // nothing here needs to know the version or the attempt count.
      requestCaptureOrganizationInBackground(captureId);
      setEditing(false);
      setDraft('');
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      reportError(caught, 'capture.editTranscript');
      setError(describeTranscriptEditError(message, errorCodeOf(caught) ?? undefined));

      if (isStaleWriteRefusal(caught)) {
        // A STALE REFUSAL RELOADS AND CLOSES THE EDITOR. What he was editing
        // no longer exists, so leaving his words in the box over text that
        // has moved would invite him to save them straight over an edit he
        // has never read. His typed correction is discarded deliberately —
        // the sentence beside it tells him why, and the current version is
        // right there to check before he tries again.
        setEditing(false);
        setDraft('');
        await onSaved();
      }
      // ANY OTHER FAILURE KEEPS THE EDITOR OPEN WITH HIS WORDS IN IT. A lost
      // connection must not also cost him the correction he just typed.
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="capture-detail-transcript" aria-labelledby="capture-transcript-heading">
      <div className="capture-note-labelrow">
        <h2 id="capture-transcript-heading" className="projects-group-heading">
          TRANSCRIPT
        </h2>
        {canEdit && !editing ? (
          <button
            type="button"
            className="capture-note-editbutton"
            onClick={startEditing}
            disabled={busy}
          >
            Edit
          </button>
        ) : null}
      </div>

      {transcript === null ? (
        // A Capture without a transcript is VALID (PT §5), and this slice does
        // not turn one into a Capture with a transcript. There is no Edit
        // above for the same reason, and the writer refuses independently.
        <p className="projects-empty-body">This Capture has no transcript.</p>
      ) : editing ? (
        <>
          <textarea
            className="capture-note-input"
            aria-label="Transcript"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            rows={8}
            disabled={busy}
          />
          <p className="capture-create-hint">
            Saving an edit keeps the previous accepted version and adds a Work Log event.
          </p>
          <div className="capture-transcript-actions">
            <button
              type="button"
              className="capture-transcript-save"
              onClick={() => {
                void save();
              }}
              // Blank is refused before a pointless round trip; the server
              // refuses it too, and the server is the authority.
              disabled={busy || blank}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="capture-note-editbutton"
              onClick={cancelEditing}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <p className="capture-detail-body">{transcript}</p>
      )}

      {error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {edited && !editing ? (
        <div className="capture-transcript-edited">
          <span className="capture-transcript-editedmark">Edited</span>
          <button
            type="button"
            className="capture-note-editbutton"
            aria-expanded={showPrevious}
            onClick={() => {
              setShowPrevious((open) => !open);
            }}
          >
            {showPrevious ? 'Hide previous version' : 'View previous version'}
          </button>
        </div>
      ) : null}

      {showPrevious && !editing ? (
        <ol className="capture-transcript-history">
          {previousVersions.map((row) => (
            <li key={row.id} className="capture-transcript-previous">
              {/* READ ONLY, AND STRUCTURALLY SO — text nodes, no control of any
                  kind. There is no Revert in M6 and no way to edit an older
                  version, here or anywhere: no writer exists that could. */}
              <p className="capture-transcript-previousmeta">
                PREVIOUS ACCEPTED VERSION
                {/* The canonical Crewlink timezone, never the device's
                    (INV-T17). Omitted rather than guessed when the profile
                    carries none — a wrong time on a site record is worse than
                    a missing one. */}
                {timeZone ? ` · ${formatInstant(row.created_at, timeZone)}` : null}
              </p>
              <p className="capture-transcript-previousbody">{row.body}</p>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
